// src/hooks/useAppRating.js
// 🌟 Native iOS App Store Rating - Uses SKStoreReviewController
// Shows the REAL Apple rating dialog inside the app

import { useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { InAppReview } from '@capacitor-community/in-app-review';

// Storage keys
const STORAGE_KEYS = {
    lastPromptDate: 'app_rating_last_prompt',
    promptCount: 'app_rating_prompt_count',
    hasRated: 'app_rating_completed',
    dismissCount: 'app_rating_dismiss_count',
    predictionCount: 'app_rating_prediction_count',
    pageVisits: 'app_rating_page_visits',
    totalTimeSpent: 'app_rating_time_spent',
};

// Configuration - Tune these for best results
const CONFIG = {
    minDaysBetweenPrompts: 7,        // Don't prompt more than once per week
    maxDismissals: 3,                 // Stop asking after 3 dismissals (note: native dialog doesn't tell us if dismissed)
    predictionsBeforePrompt: 3,       // Ask after 3rd successful prediction
    minPageVisits: 3,                 // Minimum visits to Football/Crypto pages
    minTimeOnPageSeconds: 120,        // 2 minutes on page before prompting
    maxPromptsEver: 10,               // Never prompt more than 10 times total
};

const useAppRating = () => {
    const timeOnPageRef = useRef(0);
    const pageEntryTimeRef = useRef(null);

    // ==========================================
    // HELPER: Get stored value
    // ==========================================
    const getStored = (key, defaultValue = null) => {
        try {
            const value = localStorage.getItem(key);
            return value ? JSON.parse(value) : defaultValue;
        } catch {
            return defaultValue;
        }
    };

    // ==========================================
    // HELPER: Set stored value
    // ==========================================
    const setStored = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.error('useAppRating: Storage error', e);
        }
    };

    // ==========================================
    // CHECK: Can we prompt the user?
    // ==========================================
    const canPrompt = useCallback(() => {
        // Only on native iOS or Android
        if (!Capacitor.isNativePlatform()) {
            console.log('🌟 Rating: Not native platform, skipping');
            return false;
        }

        // Already rated? Never prompt again
        if (getStored(STORAGE_KEYS.hasRated, false)) {
            console.log('🌟 Rating: User already rated');
            return false;
        }

        // Too many prompts ever? Stop
        // Note: Apple limits to 3 prompts per year per app anyway
        const promptCount = getStored(STORAGE_KEYS.promptCount, 0);
        if (promptCount >= CONFIG.maxPromptsEver) {
            console.log('🌟 Rating: Max prompts reached');
            return false;
        }

        // Check time since last prompt
        const lastPrompt = getStored(STORAGE_KEYS.lastPromptDate, 0);
        const daysSinceLastPrompt = (Date.now() - lastPrompt) / (1000 * 60 * 60 * 24);
        if (lastPrompt > 0 && daysSinceLastPrompt < CONFIG.minDaysBetweenPrompts) {
            console.log(`🌟 Rating: Too soon (${Math.round(daysSinceLastPrompt)} days since last prompt)`);
            return false;
        }

        return true;
    }, []);

    // ==========================================
    // SHOW: Native iOS/Android Rating Dialog
    // ==========================================
    const showRatingPrompt = useCallback(async () => {
        if (!canPrompt()) {
            console.log('🌟 Rating: Conditions not met, skipping prompt');
            return false;
        }

        try {
            console.log('🌟 Rating: Requesting native review dialog...');
            
            // 🔥 This triggers the REAL native iOS App Store rating dialog
            // On iOS: Shows SKStoreReviewController (the official Apple dialog)
            // On Android: Shows Google Play In-App Review
            await InAppReview.requestReview();
            
            console.log('🌟 Rating: Native review dialog requested successfully');

            // Track that we prompted
            // Note: We can't know if user actually rated (Apple doesn't tell us)
            // So we just track that we showed the dialog
            setStored(STORAGE_KEYS.lastPromptDate, Date.now());
            setStored(STORAGE_KEYS.promptCount, getStored(STORAGE_KEYS.promptCount, 0) + 1);

            return true;
        } catch (error) {
            console.error('🌟 Rating: Failed to show native dialog', error);
            
            // Fallback: If native fails, don't do anything
            // Don't fall back to URL because it's a bad UX
            return false;
        }
    }, [canPrompt]);

    // ==========================================
    // TRIGGER: After successful prediction
    // ==========================================
    const onPredictionSuccess = useCallback(() => {
        if (!Capacitor.isNativePlatform()) return;

        const currentCount = getStored(STORAGE_KEYS.predictionCount, 0) + 1;
        setStored(STORAGE_KEYS.predictionCount, currentCount);

        console.log(`🌟 Rating: Prediction count: ${currentCount}/${CONFIG.predictionsBeforePrompt}`);

        // Prompt after N successful predictions
        if (currentCount === CONFIG.predictionsBeforePrompt) {
            // Small delay so user sees the prediction first
            setTimeout(() => {
                showRatingPrompt();
            }, 2000);
        }
    }, [showRatingPrompt]);

    // ==========================================
    // TRIGGER: Track page visit (Football/Crypto)
    // ==========================================
    const onPageVisit = useCallback((pageName) => {
        if (!Capacitor.isNativePlatform()) return;

        const visits = getStored(STORAGE_KEYS.pageVisits, {});
        visits[pageName] = (visits[pageName] || 0) + 1;
        setStored(STORAGE_KEYS.pageVisits, visits);

        // Start tracking time on page
        pageEntryTimeRef.current = Date.now();

        console.log(`🌟 Rating: ${pageName} visit #${visits[pageName]}`);
    }, []);

    // ==========================================
    // TRIGGER: Check time spent on page
    // ==========================================
    const checkTimeOnPage = useCallback((pageName) => {
        if (!Capacitor.isNativePlatform()) return;
        if (!pageEntryTimeRef.current) return;

        const timeSpent = (Date.now() - pageEntryTimeRef.current) / 1000;
        const visits = getStored(STORAGE_KEYS.pageVisits, {});
        const pageVisitCount = visits[pageName] || 0;

        console.log(`🌟 Rating: Time on ${pageName}: ${Math.round(timeSpent)}s, visits: ${pageVisitCount}`);

        // Check if conditions are met
        if (
            timeSpent >= CONFIG.minTimeOnPageSeconds &&
            pageVisitCount >= CONFIG.minPageVisits
        ) {
            showRatingPrompt();
        }
    }, [showRatingPrompt]);

    // ==========================================
    // TRIGGER: User says they rated (for manual tracking)
    // ==========================================
    const onRatingCompleted = useCallback(() => {
        setStored(STORAGE_KEYS.hasRated, true);
        console.log('🌟 Rating: Marked as rated');
    }, []);

    // ==========================================
    // RESET: For testing purposes
    // ==========================================
    const resetRatingData = useCallback(() => {
        Object.values(STORAGE_KEYS).forEach(key => {
            localStorage.removeItem(key);
        });
        console.log('🌟 Rating: All data reset');
    }, []);

    // ==========================================
    // GET: Current stats (for debugging)
    // ==========================================
    const getRatingStats = useCallback(() => {
        return {
            hasRated: getStored(STORAGE_KEYS.hasRated, false),
            promptCount: getStored(STORAGE_KEYS.promptCount, 0),
            dismissCount: getStored(STORAGE_KEYS.dismissCount, 0),
            predictionCount: getStored(STORAGE_KEYS.predictionCount, 0),
            pageVisits: getStored(STORAGE_KEYS.pageVisits, {}),
            lastPromptDate: getStored(STORAGE_KEYS.lastPromptDate, 0),
            canPromptNow: canPrompt(),
            isNativePlatform: Capacitor.isNativePlatform(),
            platform: Capacitor.getPlatform(),
        };
    }, [canPrompt]);

    return {
        // Triggers
        onPredictionSuccess,
        onPageVisit,
        checkTimeOnPage,
        onRatingCompleted,
        
        // Manual
        showRatingPrompt,
        canPrompt,
        
        // Utils
        resetRatingData,
        getRatingStats,
    };
};

export default useAppRating;